import React, { useEffect, useState } from 'react';
import { TrendingUp, FileText, ClipboardList, Mail, Activity } from 'lucide-react';
import { api } from '../../lib/api';
import { fmtCents } from '../../lib/financial';

// AccountAnalyticsTab — Sprint H item 9.5.
//
// Per-account KPI grid. Pulls /api/admin/accounts/:id/analytics in one shot.
// No charts here — concise card layout suitable for the AccountDetail tab.

function fmtPct(n, d) {
  if (!d || d === 0) return '0%';
  return `${Math.round((n / d) * 100)}%`;
}

export default function AccountAnalyticsTab({ accountId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setLoading(true);
    api.get(`/api/admin/accounts/${accountId}/analytics`)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(err?.data?.error || err?.message || ''); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountId]);

  if (loading) return <div className="text-[12px] text-gray-500">Loading…</div>;
  if (error) return <div className="text-[12px] text-red-300/90">{error}</div>;
  if (!data) return null;

  const inv = data.invoices_summary || {};
  const out = data.outreach_summary || {};
  const taskOpen = data.tasks_summary?.open_count ?? 0;
  const taskDone = data.tasks_summary?.completed_30d ?? 0;
  const lastAudit = (data.audits || [])[0];

  return (
    <div data-testid="account-analytics-tab" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card icon={FileText} label="Billed (lifetime)" value={fmtCents(inv.billed_cents)} />
        <Card icon={FileText} label="Paid"             value={fmtCents(inv.paid_cents)} tone="lime" />
        <Card icon={FileText} label="Outstanding"      value={fmtCents(inv.outstanding_cents)} tone={inv.outstanding_cents > 0 ? 'amber' : null} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={TrendingUp} label="Latest score" value={lastAudit ? (lastAudit.lighthouse_score ?? lastAudit.score ?? '—') : '—'} small />
        <Card icon={ClipboardList} label="Open tasks" value={String(taskOpen)} small />
        <Card icon={ClipboardList} label="Tasks done (30d)" value={String(taskDone)} small />
        <Card icon={Activity} label="Activity (30d)" value={String(data.activity_30d ?? 0)} small />
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
          <Mail className="w-3 h-3" />
          Outreach
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Sent</div>
            <div className="text-2xl font-bold text-white" data-testid="account-outreach-sent">{out.sent_count ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Open rate</div>
            <div className="text-2xl font-bold text-white" data-testid="account-outreach-open-rate">{fmtPct(out.opens ?? 0, out.sent_count ?? 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Click rate</div>
            <div className="text-2xl font-bold text-white" data-testid="account-outreach-click-rate">{fmtPct(out.clicks ?? 0, out.sent_count ?? 0)}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
          <Activity className="w-3 h-3" />
          Stage history (last 30 transitions)
        </div>
        {(data.stage_history || []).length === 0 ? (
          <div className="text-[12px] text-gray-500">No stage transitions yet.</div>
        ) : (
          <ul className="text-[12px] divide-y divide-white/[0.04]" data-testid="account-stage-history">
            {data.stage_history.slice(0, 10).map((s, idx) => (
              <li key={idx} className="py-1.5 flex items-center justify-between">
                <span className="text-gray-300">
                  {s.from || '(none)'} <span className="text-gray-500">→</span> {s.to}
                  {s.forcedSkip && <span className="ml-2 text-[10px] text-amber-300 uppercase">forced</span>}
                </span>
                <span className="text-[11px] text-gray-500">{String(s.at).slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const TONE = {
  lime:  'border-[#B0FC38]/30 bg-[#B0FC38]/[0.04]',
  amber: 'border-amber-400/30 bg-amber-500/[0.06]',
};

function Card({ icon: Icon, label, value, tone, small }) {
  const t = TONE[tone] || 'border-white/[0.06] bg-white/[0.02]';
  return (
    <div className={`rounded-2xl border ${t} p-${small ? '4' : '5'}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`${small ? 'text-xl' : 'text-2xl'} font-bold text-white`}>{value}</div>
    </div>
  );
}
